import styled, { css, keyframes } from 'styled-components';
import type { LeakKpis, LeakSeverity, LeakSignal, RevenueLeakageResponse } from '../types';

export const money = (value: number, currency: string) => {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
  const sign = value < 0 ? '-' : '';
  return `${sign}${currency} ${formatted}`;
};

export const pct = (value: number) => `${value.toFixed(1)}%`;

export const severityLabel = (severity: LeakSeverity) => {
  if (severity === 'act_now') return 'Needs attention';
  if (severity === 'watch') return 'Keep an eye';
  return 'Fine';
};

const cardCss = css`
  background: ${({ theme }) => theme.widget?.background || theme.body};
  border: 1px solid ${({ theme }) => theme.border_color || theme.border || 'rgba(0,0,0,0.08)'};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const tableCss = css`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 20px 12px 0;
    vertical-align: middle;
  }

  th {
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.text_muted || theme.text};
  }

  td {
    border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.82rem;
    text-align: right;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  td:first-child {
    font-family: inherit;
    font-weight: 600;
    white-space: normal;
    min-width: 180px;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  .neg {
    color: #e11d48;
  }
`;

export const KpiStrip = ({ kpis, currency }: { kpis: LeakKpis; currency: string }) => {
  const leakTone = kpis.total_leakage > 0 || kpis.leak_rate_pct > 0 ? 'neg' : undefined;

  return (
    <KpiGrid>
      <Kpi $tone={leakTone}>
        <KpiValue>{money(kpis.total_leakage, currency)}</KpiValue>
        <KpiLabel>Money lost</KpiLabel>
        <KpiSub>across {kpis.orders.toLocaleString()} bills</KpiSub>
      </Kpi>
      <Kpi>
        <KpiValue>{money(kpis.net_sales, currency)}</KpiValue>
        <KpiLabel>Sales</KpiLabel>
        <KpiSub>for the dates you picked</KpiSub>
      </Kpi>
      <Kpi $tone={leakTone}>
        <KpiValue>{pct(kpis.leak_rate_pct)}</KpiValue>
        <KpiLabel>% of sales lost</KpiLabel>
        <KpiSub>how much of sales slipped away</KpiSub>
      </Kpi>
    </KpiGrid>
  );
};

export const SignalTable = ({
  signals,
  currency
}: {
  signals: LeakSignal[];
  currency: string;
}) => {
  const rows = signals.filter((s) => s.available !== false);

  if (!rows.length) {
    return <Empty>No money leakage found for these dates and locations.</Empty>;
  }

  return (
    <Panel>
      <Table>
        <thead>
          <tr>
            <th>What happened</th>
            <th>Bills</th>
            <th>Money lost</th>
            <th>% of sales</th>
            <th>Priority</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((signal) => (
            <tr key={signal.key}>
              <td>
                <SignalName>{signal.name}</SignalName>
                <SignalHint>{signal.description}</SignalHint>
              </td>
              <td>{signal.count.toLocaleString()}</td>
              <td className={signal.amount > 0 ? 'neg' : undefined}>
                {money(signal.amount, currency)}
              </td>
              <td>{pct(signal.rate_pct)}</td>
              <td>
                <StatusBadge $severity={signal.severity}>
                  {severityLabel(signal.severity)}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
};

export const LocationTable = ({
  locations,
  currency
}: {
  locations: RevenueLeakageResponse['locations'];
  currency: string;
}) => {
  if (!locations.length) {
    return <Empty>No branch leakage found for these dates.</Empty>;
  }

  return (
    <Panel>
      <Table>
        <thead>
          <tr>
            <th>Branch</th>
            <th>Sales</th>
            <th>Money lost</th>
            <th>% lost</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((loc) => (
            <tr key={loc.location_id}>
              <td>{loc.location_name || `Branch ${loc.location_id}`}</td>
              <td>{money(loc.net_sales, currency)}</td>
              <td className={loc.leakage_amount > 0 ? 'neg' : undefined}>
                {money(loc.leakage_amount, currency)}
              </td>
              <td>{pct(loc.leak_rate_pct)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
};

export const LeakSkeleton = () => (
  <SkeletonWrap aria-busy="true" aria-live="polite">
    <KpiGrid>
      {Array.from({ length: 3 }).map((_, i) => (
        <SkelCard key={`kpi-${i}`}>
          <Shimmer $width="58%" $height="22px" />
          <Shimmer $width="72%" $height="12px" />
          <Shimmer $width="44%" $height="10px" />
        </SkelCard>
      ))}
    </KpiGrid>
    <SkelPanel>
      {Array.from({ length: 6 }).map((_, i) => (
        <Shimmer key={`row-${i}`} $width="100%" $height="14px" />
      ))}
    </SkelPanel>
  </SkeletonWrap>
);

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Kpi = styled.div<{ $tone?: 'neg' }>`
  ${cardCss};
  border-radius: 12px;
  padding: 14px;
  border-left: 3px solid
    ${({ $tone, theme }) => ($tone === 'neg' ? '#e11d48' : theme.primary_color || '#0d8a6a')};
`;

const KpiValue = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.text};
`;

const KpiLabel = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary || theme.text};
  margin-top: 4px;
  font-weight: 600;
`;

const KpiSub = styled.div`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
  margin-top: 4px;
  opacity: 0.8;
`;

const Panel = styled.div`
  ${cardCss};
  border-radius: 12px;
  padding: 4px 16px 8px;
  overflow: auto;
`;

const Table = styled.table`
  ${tableCss};
`;

const SignalName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const SignalHint = styled.div`
  margin-top: 2px;
  font-size: 0.72rem;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;

const StatusBadge = styled.span<{ $severity: LeakSeverity }>`
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  font-family: inherit;
  color: ${({ $severity }) =>
    $severity === 'act_now' ? '#be123c' : $severity === 'watch' ? '#a16207' : '#15803d'};
  background: ${({ $severity }) =>
    $severity === 'act_now'
      ? 'rgba(225, 29, 72, 0.1)'
      : $severity === 'watch'
        ? 'rgba(234, 179, 8, 0.12)'
        : 'rgba(22, 163, 74, 0.1)'};
`;

const Empty = styled.div`
  ${cardCss};
  border-radius: 12px;
  padding: 24px;
  color: ${({ theme }) => theme.text_muted || theme.text};
  font-size: 0.88rem;
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SkelCard = styled.div`
  ${cardCss};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkelPanel = styled.div`
  ${cardCss};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Shimmer = styled.div<{ $width: string; $height: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(120, 120, 140, 0.08),
    rgba(120, 120, 140, 0.16),
    rgba(120, 120, 140, 0.08)
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s linear infinite;
`;
